import type { TFunction } from 'i18next';
import { ChevronLeft, ChevronRight, GitBranch, KeyRound, Plus, ShieldCheck, X } from 'lucide-react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { formatDate } from '@/lib/intl';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';
import { cn } from '@/lib/utils';
import { isStale, problemMessage, standingChanged } from '../api/problem';
import { placeFromRef, samePlace, type Place } from '../place/place';
import { Avatar, inheritedFrom } from '../ui/person-bits';
import { StateMessage } from '../ui/StateMessage';
import { moduleRank } from './module-order';
import { useRoleText } from './role-text';
import type { AssignableRole } from './use-assignable-roles';
import type { RoleAssignment } from './use-role-assignments';

export type RoleEntry = {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  /** The module the role belongs to; null for a role held here that this caller cannot assign. */
  readonly moduleCode: string | null;
  readonly assignable: AssignableRole | null;
};

export function isHere(grant: RoleAssignment, at: Place): boolean {
  const held = placeFromRef(grant.place);
  return held !== null && samePlace(held, at);
}

export function RoleList({
  roles,
  holdersByRole,
  selectedId,
  onSelect,
  placeName,
}: {
  readonly roles: readonly RoleEntry[];
  readonly holdersByRole: ReadonlyMap<string, readonly RoleAssignment[]>;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
  readonly placeName: string;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const groups = useMemo(() => {
    const map = new Map<string, RoleEntry[]>();
    for (const role of roles) {
      const key = role.moduleCode ?? 'other';
      map.set(key, [...(map.get(key) ?? []), role]);
    }
    return [...map.entries()].sort(([a], [b]) => moduleRank(a) - moduleRank(b));
  }, [roles]);

  return (
    <nav aria-label={t('rolesPage.rolesLabel', { place: placeName })} className="roles-list">
      {groups.map(([code, entries]) => (
        <div key={code} className="flex flex-col">
          <p className="role-checklist-group px-3 pt-3.5 pb-1.5">
            {code === 'other'
              ? t('rolesPage.otherRoles')
              : t(`modules.${code}` as 'modules.finance', { defaultValue: code })}
          </p>
          {entries.map((role) => {
            const holders = holdersByRole.get(role.id) ?? [];
            const people = new Set(holders.map((grant) => grant.user.id)).size;
            const current = role.id === selectedId;
            return (
              <button
                key={role.id}
                type="button"
                aria-current={current ? 'true' : undefined}
                className={cn('roles-list-item', current && 'roles-list-item-current')}
                onClick={() => onSelect(role.id)}
              >
                <span className="flex min-w-0 flex-1 flex-col text-left">
                  <span className="roles-list-name">{name(role)}</span>
                  <span className="text-muted-foreground text-[12.5px]">
                    {people === 0
                      ? t('rolesPage.nobody')
                      : t('rolesPage.holders', { count: people })}
                  </span>
                </span>
                <ChevronRight aria-hidden="true" className="text-muted-foreground size-4" />
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function RoleBadges({
  role,
  assignableKnown,
}: {
  readonly role: RoleEntry;
  readonly assignableKnown: boolean;
}) {
  const { t } = useTranslation('administration');
  return (
    <p className="mt-2 flex flex-wrap gap-2">
      {role.moduleCode !== null ? (
        <span className="status-badge info">
          {t('rolesPage.module', {
            module: t(`modules.${role.moduleCode}` as 'modules.finance', {
              defaultValue: role.moduleCode,
            }),
          })}
        </span>
      ) : null}
      {/* Whether it can be assigned here is only said once the server has said so. */}
      {assignableKnown ? (
        <span className={cn('status-badge', role.assignable !== null && 'success')}>
          {role.assignable !== null ? t('rolesPage.assignable') : t('rolesPage.notAssignable')}
        </span>
      ) : null}
    </p>
  );
}

function HoldersSection({
  holders,
  placeName,
  place,
  placeKind,
  placeId,
  onChanged,
  onStanding,
}: {
  readonly holders: readonly RoleAssignment[];
  readonly placeName: string;
  readonly place: Place;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly onChanged: () => void;
  readonly onStanding: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div>
      <h3 className="text-foreground text-[15px] font-semibold">
        {t('rolesPage.holdersHeading', { place: placeName })}
      </h3>
      {holders.length === 0 ? (
        <p className="text-muted-foreground mt-2 text-sm">{t('rolesPage.noHolders')}</p>
      ) : (
        <ul className="person-holdings">
          {holders.map((grant) => (
            <HolderItem
              key={`${grant.id}:${grant.user.id}`}
              grant={grant}
              place={place}
              placeKind={placeKind}
              placeId={placeId}
              onChanged={onChanged}
              onStanding={onStanding}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export function RoleDetail({
  role,
  holders,
  place,
  placeKind,
  placeId,
  placeName,
  canGrant,
  assignableKnown,
  onGrant,
  onBack,
  onChanged,
  onStanding,
}: {
  readonly role: RoleEntry;
  readonly holders: readonly RoleAssignment[];
  readonly place: Place;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly canGrant: boolean;
  readonly assignableKnown: boolean;
  readonly onGrant: () => void;
  readonly onBack: () => void;
  readonly onChanged: () => void;
  readonly onStanding: () => void;
}) {
  const { t } = useTranslation('administration');
  const { name, description } = useRoleText();
  const desc = description(role);
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className="role-detail">
      <span className="role-detail-back">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ChevronLeft />
          {t('rolesPage.backToRoles')}
        </Button>
      </span>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-foreground text-xl leading-tight font-bold">
            {name(role)}
          </h2>
          {desc !== null ? <p className="text-muted-foreground mt-1 text-sm">{desc}</p> : null}
          <RoleBadges role={role} assignableKnown={assignableKnown} />
        </div>
        {canGrant ? (
          <Button variant="outline" onClick={onGrant}>
            <Plus />
            {t('rolesPage.grantTo')}
          </Button>
        ) : null}
      </div>
      <HoldersSection
        holders={holders}
        placeName={placeName}
        place={place}
        placeKind={placeKind}
        placeId={placeId}
        onChanged={onChanged}
        onStanding={onStanding}
      />
    </section>
  );
}

/** Where a grant comes from: its inheritance, another place, or a direct grant, by whom and when. */
function grantSource(t: TFunction<'administration'>, grant: RoleAssignment, place: Place): string {
  const inherited = inheritedFrom(t, grant, 'sheet');
  if (inherited !== null) return inherited;
  if (!isHere(grant, place)) return t('person.holdingElsewhere', { place: grant.place.name });
  if (grant.grantedBy !== null) {
    return t('rolesPage.grantedBy', { by: grant.grantedBy, date: formatDate(grant.grantedAt) });
  }
  return t('person.holdingDirect', { date: formatDate(grant.grantedAt) });
}

function HolderTail({
  grant,
  roleName,
  pending,
  onRevoke,
}: {
  readonly grant: RoleAssignment;
  readonly roleName: string;
  readonly pending: boolean;
  readonly onRevoke: () => void;
}) {
  const { t } = useTranslation('administration');
  if (grant.can.revoke) {
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('rolesPage.revokeOne', { role: roleName, name: grant.user.displayName })}
        disabled={pending}
        onClick={onRevoke}
      >
        <X />
      </Button>
    );
  }
  if (grant.via === 'direct') return null;
  return (
    <span className="text-muted-foreground px-1.5 text-[12px] font-semibold">
      {t('person.inherited')}
    </span>
  );
}

function HolderItem({
  grant,
  place,
  placeKind,
  placeId,
  onChanged,
  onStanding,
}: {
  readonly grant: RoleAssignment;
  readonly place: Place;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly onChanged: () => void;
  readonly onStanding: () => void;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const { isPending, run } = useKeyedPendingWrite();
  const roleName = name(grant.role);
  const source = grantSource(t, grant, place);

  const revoke = () =>
    run(grant.id, async () => {
      const { error, response } = await apiClient.POST(
        '/v1/administration/{placeKind}/{placeId}/role-assignments/{assignmentId}/revocation',
        { params: { path: { placeKind, placeId, assignmentId: grant.id } }, body: {} },
      );
      if (!response.ok) {
        toast.error(problemMessage(t, error, response.status));
        if (standingChanged(error)) {
          onStanding();
        }
        if (isStale(error)) {
          onChanged();
        }
        return;
      }
      toast.success(t('person.revoked', { role: roleName, name: grant.user.displayName }));
      onChanged();
    });

  return (
    <li className="person-holding">
      <Avatar name={grant.user.displayName} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground text-[14.5px] font-semibold">
          {grant.user.displayName}
        </span>
        <span className="text-muted-foreground flex items-center gap-1.5 text-[12.5px]">
          {grant.via === 'direct' ? (
            <KeyRound aria-hidden="true" className="size-3.5 shrink-0" />
          ) : (
            <GitBranch aria-hidden="true" className="size-3.5 shrink-0" />
          )}
          {source}
        </span>
      </span>
      <HolderTail
        grant={grant}
        roleName={roleName}
        pending={isPending(grant.id)}
        onRevoke={() => void revoke()}
      />
    </li>
  );
}

export function PeopleWithRoles({
  grants,
  placeName,
  canGrant,
  onGrant,
}: {
  readonly grants: readonly RoleAssignment[];
  readonly placeName: string;
  readonly canGrant: boolean;
  readonly onGrant: (person: { id: string; displayName: string }) => void;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const people = useMemo(() => {
    const map = new Map<string, { user: RoleAssignment['user']; grants: RoleAssignment[] }>();
    for (const grant of grants) {
      const entry = map.get(grant.user.id) ?? { user: grant.user, grants: [] };
      entry.grants.push(grant);
      map.set(grant.user.id, entry);
    }
    return [...map.values()].sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));
  }, [grants]);

  if (people.length === 0) {
    return (
      <StateMessage
        icon={ShieldCheck}
        title={t('people.firstUse.title', { place: placeName })}
        text={t('people.firstUse.text')}
      />
    );
  }
  return (
    <div
      role="list"
      aria-label={t('rolesPage.peopleLabel', { place: placeName })}
      className="people-list roles-people"
    >
      {people.map(({ user, grants: held }) => (
        <div key={user.id} role="listitem" className="people-row">
          <span className="people-row-main">
            <Avatar name={user.displayName} />
            <span className="flex min-w-0 flex-col">
              <span className="people-row-name">{user.displayName}</span>
              <span className="people-row-sub">{user.email}</span>
            </span>
          </span>
          <span className="people-row-roles">
            {held.map((grant) => (
              <span
                key={`${grant.id}:${grant.user.id}`}
                className={cn('role-chip', grant.via !== 'direct' && 'role-chip-inherited')}
              >
                {grant.via !== 'direct' ? (
                  <GitBranch aria-hidden="true" className="size-3.5" />
                ) : null}
                {name(grant.role)}
              </span>
            ))}
          </span>
          <span className="people-row-actions">
            {canGrant ? (
              <Button
                variant="outline"
                size="sm"
                aria-label={t('rolesPage.grantToPerson', { name: user.displayName })}
                onClick={() => onGrant(user)}
              >
                <Plus />
                {t('rolesPage.grant')}
              </Button>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}
